import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../../services/apis/axiosInstance";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  FormControlLabel,
  Checkbox,
  DialogActions,
  IconButton,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Search,
  FilterList,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import { CONSTANTS } from "../../../../library/common/constant";
import { Pagination } from "../../../../library/component";
import styles from "./BlogListing.module.scss";
import MultiInput from "../../../../library/common/MultiInput";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { api } from "../../../../services";
import Skeleton from "@mui/material/Skeleton";
import CustomDialog from "../../../../library/common/CustomDialog";
import { throttle } from "lodash";

const {
  DELETE_CONFIRM_TITLE,
  DELETE_CONFIRM_MESSAGE,
  THROTTLE_DURATION,
  ALL_BLOGS_TITLE,
  ADD_NEW_BLOG_BUTTON,
  CATEGORY,
} = CONSTANTS;

const BlogListing = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [modalFilters, setModalFilters] = useState({});
  const [modalSelectedValues, setModalSelectedValues] = useState({});
  const [isOpen, setIsOpen] = useState({ filters: false, sort: false });
  const [categories, setCategories] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [filters, setFilters] = useState({
    pageNo: 0,
    pageSize: 10,
    search: "",
    category: "",
    language: "",
    sortBy: "",
    order: "asc",
  });
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const anchorRef = useRef(null);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const handleAddNewBlog = () => {
    navigate("/blog/add");
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const updatedFilters = {
        ...filters,
        pageNo: filters?.pageNo + 1,
      };

      const hasFilters = Object.values(updatedFilters).some(
        (value) => value !== ""
      );
      const queryString = hasFilters
        ? `?${new URLSearchParams(updatedFilters).toString()}`
        : "";

      const response = await axiosInstance.get(`/blogs${queryString}`);
      const { blogs = [] } = response?.data || {};
      setTotalBlogs(response?.meta?.total ?? 0);
      setBlogs(blogs);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helper to update filters and reset page number.
  const updateFilters = useCallback((newFilter, resetPage = true) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilter,
      pageNo: resetPage ? 0 : newFilter.pageNo ?? prev.pageNo,
    }));
  }, []);

  // Handlers for main filters.
  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      updateFilters({ [name]: value });
    },
    [updateFilters]
  );

  // Modal filter change handler.
  const handleModalFilterChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;
    // If the field returns an array (from MultiInput), join the values.
    if (["categories", "tags"].includes(name)) {
      newValue = Array.isArray(newValue) ? newValue.join(",") : newValue;
    }
    setModalFilters((prev) => ({ ...prev, [name]: newValue }));
  }, []);

  // Modal and sort handlers.
  const openFilterModal = () => setIsOpen({ filters: true, sort: false });
  const openSortModal = () => setIsOpen({ filters: false, sort: true });
  const closeModal = () => setIsOpen({ filters: false, sort: false });

  // Toggle sorting for a given field.
  const handleSortToggle = (sortField) => {
    setFilters((prev) => {
      if (prev.sortBy !== sortField) {
        return { ...prev, sortBy: sortField, order: "asc", pageNo: 0 };
      } else {
        if (prev.order === "asc") {
          return { ...prev, order: "desc", pageNo: 0 };
        } else if (prev.order === "desc") {
          return { ...prev, sortBy: "", order: "asc", pageNo: 0 };
        }
      }
      return prev;
    });
  };

  // Apply modal filters to main filters.
  const applyModalFilters = () => {
    updateFilters(modalFilters);
    closeModal();
  };

  // Pagination handlers.
  const handlePageChange = (event, newPage) => {
    updateFilters({ pageNo: newPage }, false);
  };

  const handleRowsPerPageChange = (event) => {
    updateFilters({ pageSize: parseInt(event.target.value, 10) });
  };

  const handleOpenDeleteDialogue = (id) => {
    setDialogOpen(true);
    setDeleteItemId(id);
  };

  const handleCloseDeleteDialogue = () => {
    setDialogOpen(false);
  };

  const deleteBlog = throttle(async () => {
    try {
      setIsLoading(true);
      await api.delete(`/blogs/${deleteItemId}`);
      await fetchData();
      handleCloseDeleteDialogue();
    } catch (error) {
      console.error("Failed to delete: ", error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const fetchCategories = async () => {
    try {
      const { data } = await api.get("/categories/?isBlog=true");
      setCategories(data?.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <Paper className={styles.paper_container}>
      <Box className={styles.header_search_add_container}>
        <Typography className={styles.header_title} textAlign="center">
          {ALL_BLOGS_TITLE}
        </Typography>
      </Box>
      <Box
        className={styles.search_add_container}
        sx={{ display: "flex", gap: 2, alignItems: "center" }}
      >
        <TextField
          className={styles.search_field}
          placeholder="Search Blogs"
          variant="outlined"
          size="small"
          name="search"
          value={filters?.search}
          onChange={handleFilterChange}
          sx={{ minWidth: 800 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          color="primary"
          variant="contained"
          size="medium"
          onClick={handleAddNewBlog}
        >
          {ADD_NEW_BLOG_BUTTON}
        </Button>
        <Button ref={anchorRef} variant="outlined" onClick={openSortModal}>
          Sort By
        </Button>
        <Menu
          open={isOpen.sort}
          anchorEl={anchorRef.current}
          onClose={closeModal}
          disableAutoFocusItem
        >
          <MenuItem onClick={() => handleSortToggle("createdAt")}>
            <Box display="flex" alignItems="center" width="100%">
              <span>Created Date</span>
              {filters?.sortBy === "createdAt" && (
                <Box ml="auto">
                  {filters?.order === "asc" ? (
                    <ArrowUpward fontSize="small" />
                  ) : (
                    <ArrowDownward fontSize="small" />
                  )}
                </Box>
              )}
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleSortToggle("title.en")}>
            <Box display="flex" alignItems="center" width="100%">
              <span>Title</span>
              {filters?.sortBy === "title.en" && (
                <Box ml="auto">
                  {filters?.order === "asc" ? (
                    <ArrowUpward fontSize="small" />
                  ) : (
                    <ArrowDownward fontSize="small" />
                  )}
                </Box>
              )}
            </Box>
          </MenuItem>
        </Menu>
        <IconButton
          onClick={openFilterModal}
          className={styles.filterButton}
          color="primary"
        >
          <FilterList />
        </IconButton>
      </Box>

      {/* Filters Modal */}
      <Dialog open={isOpen.filters} onClose={closeModal}>
        <DialogTitle>Filters</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <MultiInput
              options={categories}
              initialValues={modalSelectedValues?.categories || []}
              onChange={handleModalFilterChange}
              name="categories"
              label={CATEGORY}
              type="object"
              setFinalValues={setModalSelectedValues}
              select
            />
            <MultiInput
              onChange={handleModalFilterChange}
              initialValues={modalSelectedValues?.tags || []}
              name="tags"
              label="Tags"
              setFinalValues={setModalSelectedValues}
              select
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="featured"
                  checked={modalFilters?.featured || false}
                  onChange={handleModalFilterChange}
                />
              }
              label="Only Featured Blogs"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="upcoming"
                  checked={modalFilters?.upcoming || false}
                  onChange={handleModalFilterChange}
                />
              }
              label="Only Upcoming Blogs"
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="blogSeries"
                  checked={modalFilters?.blogSeries || false}
                  onChange={handleModalFilterChange}
                />
              }
              label="Only Blogs from BlogSeries"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            onClick={applyModalFilters}
            variant="contained"
            color="primary"
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer>
        <Table stickyHeader aria-label="blog table">
          <TableHead>
            <TableRow>
              <TableCell className={styles.table_header_cell}>S.No</TableCell>
              <TableCell className={styles.table_header_cell}>Title</TableCell>
              <TableCell className={styles.table_header_cell}>Author</TableCell>
              <TableCell className={styles.table_header_cell}>
                Category
              </TableCell>
              <TableCell className={styles.table_header_cell}>
                Published Date
              </TableCell>
              <TableCell className={styles.table_header_cell}>Status</TableCell>
              <TableCell className={styles.table_header_cell}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  {Array.from({ length: 7 }).map((_, idx) => (
                    <TableCell key={idx}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : blogs?.length > 0 ? (
              blogs?.map((blog, index) => (
                <TableRow key={blog._id}>
                  <TableCell>
                    {filters?.pageSize * filters?.pageNo + index + 1}
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography>{blog.title.en}</Typography>
                      <Stack direction="row" gap={1}>
                        {blog.isFeatured && (
                          <Chip
                            size="small"
                            label="Featured"
                            sx={{
                              backgroundColor: "#ffe082",
                              color: "#b26a00",
                              borderRadius: "8px",
                              fontWeight: 600,
                              fontSize: 12,
                              height: 24,
                            }}
                          />
                        )}
                        {new Date(blog.publishDate) > new Date() && (
                          <Chip
                            color="secondary"
                            size="small"
                            label="Upcoming"
                            sx={{
                              backgroundColor: "#b2ebf2",
                              color: "#006064",
                              borderRadius: "8px",
                              fontWeight: 600,
                              fontSize: 12,
                              height: 24,
                            }}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell>{blog?.author?.name?.en}</TableCell>
                  <TableCell>
                    {blog?.categories?.map((cat) => cat.name.en).join(", ")}
                  </TableCell>
                  <TableCell>
                    {new Date(blog?.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{blog?.status}</TableCell>
                  <TableCell className={styles.icon_button_container}>
                    <div className={styles.pageButtons}>
                      <IconButton
                        className={styles.edit_icon}
                        onClick={() => navigate(`/blog/edit/${blog?.slug}`)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        className={styles.delete_icon}
                        onClick={() => handleOpenDeleteDialogue(blog?._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className={styles.no_data_cell}>
                  <Typography variant="h6" align="center">
                    No blogs available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        totalCount={totalBlogs}
        pageSize={filters?.pageSize}
        pageNo={filters?.pageNo}
        pageChangeHandler={handlePageChange}
        rowsPerPageHandler={handleRowsPerPageChange}
      />

      <CustomDialog
        open={isDialogOpen}
        title={DELETE_CONFIRM_TITLE}
        content={DELETE_CONFIRM_MESSAGE}
        onClose={handleCloseDeleteDialogue}
        onPrimaryClick={deleteBlog}
        onSecondaryClick={handleCloseDeleteDialogue}
        disablePrimary={isLoading}
      />

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default BlogListing;
